import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiAxios from 'helpers/apiAxios';
import { handleAsyncThunkError } from 'redux/reduxHelpers';

const BASE = '/crm/dashboard/job_entries';

const makeThunk = (key, path, message) => createAsyncThunk(
  `dashboardJobEntry/${key}`,
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/${path}`, { params });
      return res.data.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, message));
    }
  }
);

export const fetchJobEntrySummary        = makeThunk('fetchSummary',        'get_summary_data',         'Failed to fetch job entry summary');
export const fetchJobEntryTrend          = makeThunk('fetchTrend',          'get_trend_data',           'Failed to fetch job entry trend');
export const fetchJobEntryCategory       = makeThunk('fetchCategory',       'get_category_data',        'Failed to fetch job entry categories');
export const fetchJobEntryCategoryByType = makeThunk('fetchCategoryByType', 'get_category_by_type_data', 'Failed to fetch classification by type');
export const fetchJobEntryRegion         = makeThunk('fetchRegion',         'get_region_data',          'Failed to fetch region stats');
export const fetchJobEntrySalesUnit      = makeThunk('fetchSalesUnit',      'get_sales_unit_data',      'Failed to fetch sales unit stats');
export const fetchJobEntryCountry        = makeThunk('fetchCountry',        'get_country_data',         'Failed to fetch country stats');
export const fetchJobEntryProvince       = makeThunk('fetchProvince',       'get_province_data',        'Failed to fetch province stats');
export const fetchJobEntryDomesticOversea = makeThunk('fetchDomesticOversea', 'get_domestic_oversea_data', 'Failed to fetch domestic/oversea stats');
export const fetchJobEntryFilterOptions  = makeThunk('fetchFilterOptions',  'filter_options',           'Failed to fetch filter options');

export const fetchJobEntryDetailed = createAsyncThunk(
  'dashboardJobEntry/fetchDetailed',
  async (params, { rejectWithValue }) => {
    try {
      const res = await apiAxios.get(`${BASE}/get_detailed_data`, { params });
      return res.data;
    } catch (e) {
      return rejectWithValue(handleAsyncThunkError(e, 'Failed to fetch job entry records'));
    }
  }
);

const makeSection = (arrayDefault = false) => ({
  data:    arrayDefault ? [] : null,
  loading: false,
  error:   null
});

const initialState = {
  summary:         makeSection(),
  trend:           makeSection(true),
  category:        makeSection(true),
  categoryByType:  makeSection(true),
  region:          makeSection(true),
  salesUnit:       makeSection(true),
  country:         makeSection(true),
  province:        makeSection(true),
  domesticOversea: makeSection(true),
  filterOptions:   makeSection(),
  detailed: {
    rows:      [],
    totalRows: 0,
    page:      1,
    perPage:   50,
    loading:   false,
    error:     null
  },
  lastUpdated: null
};

const thunkMap = [
  ['summary',         fetchJobEntrySummary],
  ['trend',           fetchJobEntryTrend],
  ['category',        fetchJobEntryCategory],
  ['categoryByType',  fetchJobEntryCategoryByType],
  ['region',          fetchJobEntryRegion],
  ['salesUnit',       fetchJobEntrySalesUnit],
  ['country',         fetchJobEntryCountry],
  ['province',        fetchJobEntryProvince],
  ['domesticOversea', fetchJobEntryDomesticOversea],
  ['filterOptions',   fetchJobEntryFilterOptions],
];

const dashboardJobEntrySlice = createSlice({
  name: 'dashboardJobEntry',
  initialState,
  reducers: {
    resetJobEntryDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    thunkMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, (state) => {
          state[key].loading = true;
          state[key].error   = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key].data    = action.payload;
          state[key].loading = false;
          state.lastUpdated  = new Date().toISOString();
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error   = action.payload;
        });
    });

    builder
      .addCase(fetchJobEntryDetailed.pending, (state) => {
        state.detailed.loading = true;
        state.detailed.error   = null;
      })
      .addCase(fetchJobEntryDetailed.fulfilled, (state, action) => {
        state.detailed.rows      = action.payload.rows || [];
        state.detailed.totalRows = action.payload.total_rows || 0;
        state.detailed.page      = action.payload.page || 1;
        state.detailed.perPage   = action.payload.per_page || 50;
        state.detailed.loading   = false;
        state.lastUpdated        = new Date().toISOString();
      })
      .addCase(fetchJobEntryDetailed.rejected, (state, action) => {
        state.detailed.loading = false;
        state.detailed.error   = action.payload;
      });
  }
});

export const { resetJobEntryDashboard } = dashboardJobEntrySlice.actions;
export default dashboardJobEntrySlice.reducer;
